// src/pages/task/TaskTemplateListPage.tsx

import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTaskTemplate, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

/**
 * Task template list page.
 */
export const TaskTemplateListPage = observer(() => {
  const store = useTaskTemplate();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    store.ListTemplates({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
    });
  }, [searchParams]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm">
            <Link to="/tasks" className="text-muted-foreground hover:text-foreground">
              {t('task.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('task.templates')}</span>
          </nav>
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('task.templates')}</h1>
        </div>
        <Link to="/task-templates/new">
          <Button>{t('task.createTemplate')}</Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <div className="overflow-x-auto">
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('common.description')}</TableHead>
              <TableHead>{t('task.defaultType')}</TableHead>
              <TableHead>{t('task.defaultPriority')}</TableHead>
              <TableHead>{t('common.active')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <>
                {Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 w-32 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 w-40 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 w-16 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 w-16 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-5 w-14 bg-muted rounded-full animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 w-24 bg-muted rounded animate-pulse ml-auto" /></TableCell>
                  </TableRow>
                ))}
              </>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message={t('task.noTemplates')} />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Link
                      to={`/task-templates/${item.entity?.id || item.id}`}
                      className="text-primary hover:underline font-medium"
                    >
                      {String(item.name ?? '')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {String(item.description ?? '')}
                  </TableCell>
                  <TableCell>
                    {item.default_type
                      ? formatLabel(String(item.default_type), 'TASK_TYPE_')
                      : '--'}
                  </TableCell>
                  <TableCell>
                    {item.default_priority
                      ? formatLabel(String(item.default_priority), 'TASK_PRIORITY_')
                      : '--'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? 'success' : 'secondary'}>
                      {item.is_active ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/task-templates/${item.entity?.id || item.id}`}>
                        <Button variant="ghost" size="sm">{t('common.view')}</Button>
                      </Link>
                      <Link to={`/task-templates/${item.entity?.id || item.id}/edit`}>
                        <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/tasks/new?template=${item.entity?.id || item.id}`)}
                      >
                        {t('task.createTask')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </Card>
    </div>
  );
});

export default TaskTemplateListPage;
